/**
 * Request Calendar Section Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestCalendar
 */

import React, { useMemo, useCallback } from 'react';
import { Calendar } from '@dynamon-as/ui';
import type { RangeDateValue } from '@dynamon-as/ui';
import { CalendarMonth } from '@mui/icons-material';
import { formatDate } from '@features/requests/utils';
import { useRequestTranslations, useI18n } from '@shared/i18n';
import type { Request } from '@features/requests/types';
import { RequestSource } from '@features/requests/types';
import styles from '../RequestDetails.module.css';

/**
 * Props for RequestCalendar component
 */
export interface RequestCalendarProps {
    request: Request;
}

/**
 * Displays calendar with selected dates
 */
export function RequestCalendar({
    request,
}: RequestCalendarProps): React.ReactElement | null {
    const { t } = useI18n();
    const { getShiftPreferenceLabel } = useRequestTranslations();

    const hasSelectedDates = request.startDate && request.endDate;
    const calendarMode = 'double';

    const calendarValue = useMemo((): RangeDateValue | null => {
        if (!hasSelectedDates) return null;
        if (!request.startDate || !request.endDate) return null;
        return {
            start: request.startDate,
            end: request.endDate,
        };
    }, [request.startDate, request.endDate, hasSelectedDates]);

    const disabledDates = useMemo((): Date[] => {
        const dates: Date[] = [];
        if (request.excludedPeriods) {
            dates.push(...request.excludedPeriods);
        }
        if (request.excludedDates) {
            dates.push(...request.excludedDates);
        }
        return dates;
    }, [request.excludedPeriods, request.excludedDates]);

    const handleCalendarChange = useCallback((): void => {
        // Calendar is in read-only mode
    }, []);

    if (!hasSelectedDates) return null;

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
                <CalendarMonth
                    fontSize="small"
                    className={styles.sectionIcon}
                />
                {t('requests.details.selectedDates')}
            </h3>
            <div className={styles.calendarContainer}>
                <Calendar
                    mode={calendarMode}
                    selectionType="range"
                    value={calendarValue}
                    onChange={handleCalendarChange}
                    disabledDates={disabledDates}
                    excludedDates={request.excludedDates}
                    excludeModeEnabled={false}
                    showWeekNumbers={true}
                    locale="nb"
                />
            </div>

            <div className={styles.dateInfo}>
                {request.type === RequestSource.WISH &&
                    request.shiftPreference && (
                        <div className={styles.detail}>
                            <span className={styles.detailLabel}>
                                {t('requests.details.shiftPreference')}:{' '}
                            </span>
                            <span className={styles.detailValue}>
                                {getShiftPreferenceLabel(
                                    request.shiftPreference,
                                )}
                            </span>
                        </div>
                    )}

                {request.excludedDates && request.excludedDates.length > 0 && (
                    <div className={styles.detail}>
                        <span className={styles.detailLabel}>
                            {t('requests.details.excludedDays')}:{' '}
                        </span>
                        <span className={styles.detailValue}>
                            {request.excludedDates
                                .map((d) => formatDate(d))
                                .join(', ')}
                        </span>
                    </div>
                )}

                {request.excludedPeriods &&
                    request.excludedPeriods.length > 0 && (
                        <div className={styles.detail}>
                            <span className={styles.detailLabel}>
                                {t('requests.details.excludedPeriods')}:{' '}
                            </span>
                            <span className={styles.detailValue}>
                                {request.excludedPeriods
                                    .map((p) => formatDate(p))
                                    .join(', ')}
                            </span>
                        </div>
                    )}
            </div>
        </div>
    );
}
